import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { Request } from '@warp-drive/ember';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';
import Card from 'tio-ui/components/card';
import Button from 'tio-ui/components/button';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { PhArrowRight } from 'ember-phosphor-icons';
import { programTemplateSchoolsQuery } from 'tio-data/requests/lpn-page';
import { discountLetterSchoolsQuery } from 'tio-data/requests/lpn-discount-letter';
import type { TasProgramTemplateInstitutionExtension as TasProgramTemplateInstitution } from 'tio-data/data/resources/tas-program-template-institution.ext';
import type { LpnDiscountLetterSchool } from 'tio-data/data/resources/lpn-discount-letter-school.type';
import LpnSchoolsDrawer from 'tio-employee/components/lpn/schools-drawer';
import DiscountLetterModal from 'tio-employee/components/lpn/discount-letter-modal';
import type { EligibleTemplate } from 'tio-employee/services/reimagined-data';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';

interface TasDashboardLpnPromoSignature {
  Args: {
    /** The programs the employee can apply to; decides whether and which LPN schools apply. */
    eligibleTemplates: readonly EligibleTemplate[];
  };
}

/**
 * The LPN identifiers (`dapip_<id>` / `tas_<id>`) the employee's curated programs allow that the
 * company also offers through LPN.
 */
export function curatedLpnIdentifiers(
  links: readonly TasProgramTemplateInstitution[],
  offered: readonly LpnDiscountLetterSchool[]
): string[] {
  const offeredIds = new Set(offered.map((school) => school.institutionIdentifier));
  const curated = links.flatMap((link) => {
    const ids: string[] = [];
    if (link.accreditedInstitution?.id) ids.push(`dapip_${link.accreditedInstitution.id}`);
    if (link.unaccreditedInstitution?.id) ids.push(`tas_${link.unaccreditedInstitution.id}`);
    return ids;
  });
  return [...new Set(curated)].filter((id) => offeredIds.has(id));
}

/**
 * Promotes LPN discounts on the TA dashboard before the employee starts an application. Shown when
 * LPN is on and any eligible program can use an LPN school: one that allows every school, or a
 * curated list that includes one. Curated-only employees browse just those schools.
 */
export default class TasDashboardLpnPromo extends Component<TasDashboardLpnPromoSignature> {
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;

  get companySettingId() {
    return this.partner.companySettings?.id ?? null;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? null;
  }

  get isAvailable() {
    const lpn = this.partner.companySettings?.lpn;
    const lpnOn = Boolean(lpn && 'isEnabled' in lpn && lpn.isEnabled);
    return (
      lpnOn &&
      Boolean(this.companySettingId && this.employeeId) &&
      this.args.eligibleTemplates.length > 0
    );
  }

  get allowsAnySchool() {
    return this.args.eligibleTemplates.some((template) => !template.institutionListIsExclusive);
  }

  @cached
  get curatedSchoolsQuery() {
    return programTemplateSchoolsQuery(
      this.args.eligibleTemplates.map((template) => template.id).filter(Boolean)
    );
  }

  @cached
  get offeredSchoolsQuery() {
    return this.employeeId ? discountLetterSchoolsQuery(this.employeeId) : null;
  }

  <template>
    {{#if this.isAvailable}}
      {{#if this.allowsAnySchool}}
        <LpnPromoSection
          @companySettingId={{this.companySettingId}}
          @institutionIdentifiers={{null}}
        />
      {{else if this.offeredSchoolsQuery}}
        {{! A failed check hides the section rather than the dashboard. }}
        <Request @query={{this.curatedSchoolsQuery}}>
          <:error></:error>
          <:content as |curated|>
            <Request @query={{this.offeredSchoolsQuery}}>
              <:error></:error>
              <:content as |offered|>
                {{#let (curatedLpnIdentifiers curated.data offered.data) as |identifiers|}}
                  {{#if identifiers.length}}
                    <LpnPromoSection
                      @companySettingId={{this.companySettingId}}
                      @institutionIdentifiers={{identifiers}}
                    />
                  {{/if}}
                {{/let}}
              </:content>
            </Request>
          </:content>
        </Request>
      {{/if}}
    {{/if}}
  </template>
}

interface LpnPromoSectionSignature {
  Args: {
    /** The viewer's company setting; the drawer is not rendered without one. */
    companySettingId: string | null;
    /**
     * Curated LPN identifiers to limit the drawer to; null lists every school the company offers.
     * When set, the section does not link to the unrestricted /lpn catalog.
     */
    institutionIdentifiers: readonly string[] | null;
  };
}

class LpnPromoSection extends Component<LpnPromoSectionSignature> {
  @tracked isDrawerOpen = false;
  @tracked isLetterOpen = false;

  openDrawer = () => {
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
  };

  openLetter = () => {
    this.isLetterOpen = true;
  };

  closeLetter = () => {
    this.isLetterOpen = false;
  };

  <template>
    <Section data-test-tas-dashboard-lpn-promo>
      <:header>
        {{t "lpn.dashboard_promo.section_title"}}
      </:header>
      <:body>
        <Card @padding="md" @radius="nested" class="tio-col gap-4">
          <Heading @level={{3}} @role="panel-title">{{t "lpn.dashboard_promo.title"}}</Heading>
          <Text @tag="p" @role="body-secondary" class="max-w-3xl">
            {{t "lpn.dashboard_promo.body_before"}}
            {{! The /lpn catalog is unrestricted, so curated-only employees get plain text, not a link. }}
            {{#if @institutionIdentifiers}}{{t "lpn.dashboard_promo.body_link"}}{{else}}<LinkTo
                @route="authenticated.lpn.index"
                class="tio-fg-brand tio-text-sm-semibold underline"
                data-test-tas-dashboard-lpn-page-link
              >{{t "lpn.dashboard_promo.body_link"}}</LinkTo>{{/if}}{{t
              "lpn.dashboard_promo.body_after"
            }}
          </Text>
          <div class="tio-col-tight">
            <p class="tio-text-sm-semibold tio-fg-primary">{{t
                "lpn.dashboard_promo.how_it_works"
              }}</p>
            <ol class="list-decimal pl-5 tio-text-sm-normal tio-fg-secondary space-y-1">
              <li>{{t "lpn.dashboard_promo.step_browse"}}</li>
              <li>{{t "lpn.dashboard_promo.step_mention"}}</li>
              <li>{{t "lpn.dashboard_promo.step_apply"}}</li>
            </ol>
          </div>
          <p class="tio-text-sm-normal tio-fg-secondary">
            <em>{{t "lpn.dashboard_promo.proof_prefix"}}</em>
            <strong>{{t "lpn.dashboard_promo.proof_provide"}}</strong>
            <Button
              class="inline tio-fg-brand"
              @appearance="link"
              {{on "click" this.openLetter}}
              data-test-tas-dashboard-lpn-letter
            >
              {{! The button's content wrapper resets text-decoration, so the label carries it. }}
              <span class="tio-text-sm-semibold italic underline">{{t
                  "lpn.dashboard_promo.proof_link"
                }}</span>
            </Button>
            <strong>{{t "lpn.dashboard_promo.proof_suffix"}}</strong>
          </p>
          <Button
            class="self-start"
            @intent="primary"
            @appearance="outlined"
            @label={{t "lpn.dashboard_promo.browse"}}
            @icon={{PhArrowRight}}
            @iconPlacement="end"
            {{on "click" this.openDrawer}}
            data-test-tas-dashboard-lpn-browse
          />
        </Card>
      </:body>
    </Section>

    {{#if @companySettingId}}
      <LpnSchoolsDrawer
        @isOpen={{this.isDrawerOpen}}
        @onClose={{this.closeDrawer}}
        @companySettingId={{@companySettingId}}
        @institutionIdentifiers={{@institutionIdentifiers}}
      />
    {{/if}}
    {{#if this.isLetterOpen}}
      <DiscountLetterModal @source="tas_dashboard" @onClose={{this.closeLetter}} />
    {{/if}}
  </template>
}
